import { createFileRoute, useNavigate } from "@tanstack/react-router";

export const Route = createFileRoute("/admin/_layout/attendance/adjustments")({
  component: RouteComponent,
});

function RouteComponent() {
  // AdminAttendanceAdjustmentViewSet is a whole-class gate with no
  // degrade-to-own-rows fallback, so the whole page is protected up front.
  return (
    <ProtectedRoute requiredPermission="attendance.timesheet.adjust">
      <AttendanceAdjustmentDataTable />
    </ProtectedRoute>
  );
}

import { useState, useEffect, useMemo, useCallback } from "react";
import { toast } from "sonner";
import { Controller, useForm } from "react-hook-form";

import { ProtectedRoute } from "@/components/protected-route";
import { SidebarInset } from "@/components/ui/sidebar";
import { DashboardHeader } from "@/components/dashboard-header";
import { PaginationComponent } from "@/components/crud/Pagination/Pagination";
import { PAGE_SIZE_OPTIONS } from "@/lib/constants";
import { usePaginationState } from "@/hooks/ui/usePaginationState";
import { SelectFilter } from "@/components/crud/filters/SelectFilter";

import useFetchAdjustmentRequests from "@/hooks/api/attendance-adjustments/useFetchAdjustmentRequests";
import useApproveAttendanceAdjustment from "@/hooks/api/attendance-adjustments/useApproveAttendanceAdjustment";
import useRejectAttendanceAdjustment from "@/hooks/api/attendance-adjustments/useRejectAttendanceAdjustment";
import useApproveBulkAdjustment from "@/hooks/api/attendance-adjustments/useApproveBulkAdjustment";
import useRejectBulkAdjustment from "@/hooks/api/attendance-adjustments/useRejectBulkAdjustment";
import useCancelApprovedAdjustment from "@/hooks/api/attendance-adjustments/useCancelApprovedAdjustment";
import useCancelApprovedBulkAdjustment from "@/hooks/api/attendance-adjustments/useCancelApprovedBulkAdjustment";
import useAutoCompleteUser from "@/hooks/api/user/useAutoCompleteUser";

import { AdjustmentRequestTable } from "@/components/crud/table/AdjustmentRequestTable";
import { AttendanceAdjustmentDetailSheet } from "@/components/crud/sheets/AttendanceAdjustmentDetailSheet";
import { TimesheetBulkAdjustmentDetailSheet } from "@/components/crud/sheets/TimesheetBulkAdjustmentDetailSheet";
import { DirectAdjustmentDialog } from "@/components/crud/modals/DirectAdjustmentDialog";

import PageHeader from "@/components/reusable-components/header/page-header";
import FiltersPanel from "@/components/reusable-components/header/filter-pannel";
import SelectUser from "@/components/reusable-components/select-user";
import { useDebounce } from "@/hooks/use-debounce";

import type {
  AttendanceAdjustment,
  AttendanceAdjustmentStatus,
  CombinedAdjustmentRequest,
  TimesheetBulkAdjustment,
} from "@/types/attendance-adjustment";

function AttendanceAdjustmentDataTable() {
  const navigate = useNavigate();
  const [autoCompleteSearch, setAutoCompleteSearch] = useState("");
  const autoCompleteQuery = useAutoCompleteUser({
    search: autoCompleteSearch || undefined,
  });
  const users = useMemo(
    () => autoCompleteQuery.data?.pages.flatMap((p) => p.results) ?? [],
    [autoCompleteQuery.data]
  );
  const autoCompleteLoading = autoCompleteQuery.isFetching;

  const [statusFilter, setStatusFilter] = useState<
    AttendanceAdjustmentStatus | "clear" | undefined
  >();

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery] = useDebounce(searchQuery, 500);
  const { page, setPage, pageSize, handlePageSizeChange } =
    usePaginationState();

  const { setValue, watch, control } = useForm<{ user_id?: number }>({
    defaultValues: { user_id: undefined },
  });
  const watchedUserId = watch("user_id");

  const [selectedAdjustment, setSelectedAdjustment] =
    useState<CombinedAdjustmentRequest | null>(null);
  const [isDetailSheetOpen, setIsDetailSheetOpen] = useState(false);
  const [isReviewMode, setIsReviewMode] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);

  const adjustmentsQuery = useFetchAdjustmentRequests(
    {
      page_size: pageSize,
      page,
      search: debouncedSearchQuery || undefined,
      status: statusFilter === "clear" ? undefined : statusFilter,
      user_id: watchedUserId || undefined,
    },
    true
  );
  const adjustments = adjustmentsQuery.data?.results ?? [];
  const count = adjustmentsQuery.data?.count ?? 0;

  const approveAdjustment = useApproveAttendanceAdjustment();
  const rejectAdjustment = useRejectAttendanceAdjustment();
  const approveBulkAdjustment = useApproveBulkAdjustment();
  const rejectBulkAdjustment = useRejectBulkAdjustment();
  const cancelApprovedAdjustment = useCancelApprovedAdjustment();
  const cancelApprovedBulkAdjustment = useCancelApprovedBulkAdjustment();

  const isMutating =
    approveAdjustment.isPending ||
    rejectAdjustment.isPending ||
    approveBulkAdjustment.isPending ||
    rejectBulkAdjustment.isPending ||
    cancelApprovedAdjustment.isPending ||
    cancelApprovedBulkAdjustment.isPending;
  const isLoading = adjustmentsQuery.isPending || isMutating;
  const isFetching = adjustmentsQuery.isFetching || isMutating;

  // Back to page 1 whenever a filter changes
  useEffect(() => {
    setPage(1);
  }, [debouncedSearchQuery, statusFilter, watchedUserId, setPage]);

  const totalPages = useMemo(
    () => (count ? Math.ceil(count / pageSize) : 1),
    [count, pageSize]
  );

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  const resetFilters = () => {
    setStatusFilter(undefined);
    setSearchQuery("");
    setValue("user_id", undefined);
    setPage(1);
  };

  const handleViewDetails = (adjustment: CombinedAdjustmentRequest) => {
    setSelectedAdjustment(adjustment);
    setIsReviewMode(false);
    setIsDetailSheetOpen(true);
  };

  const handleReviewRequest = (adjustment: CombinedAdjustmentRequest) => {
    setSelectedAdjustment(adjustment);
    setIsReviewMode(true);
    setIsDetailSheetOpen(true);
  };

  // Approve/reject route to whichever mutation matches the selected row's
  // type — the detail sheet below is likewise picked by that same type,
  // so this and that stay in sync by construction.
  const handleApprove = async (id: string, remarks: string) => {
    if (selectedAdjustment?.request_type === "FULL_DAY") {
      await approveBulkAdjustment.mutateAsync({ id, payload: { remarks } });
    } else {
      await approveAdjustment.mutateAsync({ id, payload: { remarks } });
    }
    toast.success("Adjustment request approved");
  };

  const handleReject = async (id: string, rejectionReason: string) => {
    const payload = { rejection_reason: rejectionReason };
    if (selectedAdjustment?.request_type === "FULL_DAY") {
      await rejectBulkAdjustment.mutateAsync({ id, payload });
    } else {
      await rejectAdjustment.mutateAsync({ id, payload });
    }
    toast.success("Adjustment request rejected");
  };

  // Same type routing as approve/reject: the sheet that rendered the row
  // is the one whose mutation undoes it.
  const handleCancelApproved = async (id: string, remarks: string) => {
    const payload = { remarks };
    if (selectedAdjustment?.request_type === "FULL_DAY") {
      await cancelApprovedBulkAdjustment.mutateAsync({ id, payload });
    } else {
      await cancelApprovedAdjustment.mutateAsync({ id, payload });
    }
    toast.success("Approved adjustment cancelled and the day restored");
  };

  const handleSearchUsers = useCallback((query: string) => {
    setAutoCompleteSearch(query);
  }, []);

  // Narrow the discriminated union explicitly (rather than in the JSX
  // ternary below) so each detail sheet gets its own concrete type.
  let singleAdjustment: AttendanceAdjustment | null = null;
  let bulkAdjustment: TimesheetBulkAdjustment | null = null;
  if (selectedAdjustment?.request_type === "FULL_DAY") {
    bulkAdjustment = selectedAdjustment;
  } else if (selectedAdjustment) {
    singleAdjustment = selectedAdjustment;
  }

  return (
    <SidebarInset>
      <DashboardHeader />

      <div className="space-y-4 p-6 pt-0">
        <PageHeader
          title="Attendance Adjustments"
          description="Review and process employee attendance adjustment requests."
          onToggleFilter={() => setShowFilters((prev) => !prev)}
          onAdd={() => setIsCreateDialogOpen(true)}
          addLabel="Create Adjustment"
          isLoading={isFetching}
        />

        <FiltersPanel
          show={showFilters}
          onClear={resetFilters}
          isLoading={isFetching}
        >
          <Controller
            name="user_id"
            control={control}
            render={() => (
              <SelectUser
                watchedUserId={watchedUserId}
                users={users}
                onSearchUsers={handleSearchUsers}
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                setValue={setValue}
                isLoading={autoCompleteLoading}
                fetchNextPage={autoCompleteQuery.fetchNextPage}
                hasMore={autoCompleteQuery.hasNextPage}
              />
            )}
          />

          <SelectFilter
            placeholder="Status"
            options={[
              { label: "Pending", value: "PENDING" },
              { label: "Approved", value: "APPROVED" },
              { label: "Rejected", value: "REJECTED" },
              { label: "Cancelled", value: "CANCELLED" },
              { label: "All", value: "clear" },
            ]}
            value={statusFilter}
            onChange={setStatusFilter}
          />
        </FiltersPanel>

        <AdjustmentRequestTable
          adjustments={adjustments}
          isLoading={isLoading}
          onViewDetails={handleViewDetails}
          onReviewRequest={handleReviewRequest}
          onUserClick={(adjustment) =>
            navigate({
              to: "/admin/users/$id",
              params: { id: String(adjustment.user.id) },
              search: { tab: "attendance-adjustments" },
            })
          }
        />

        <PaginationComponent
          count={count}
          page={page}
          onPageChange={handlePageChange}
          isLoading={isFetching}
          pageSize={pageSize}
          onPageSizeChange={handlePageSizeChange}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
        />

        {bulkAdjustment ? (
          <TimesheetBulkAdjustmentDetailSheet
            isOpen={isDetailSheetOpen}
            onClose={() => setIsDetailSheetOpen(false)}
            adjustment={bulkAdjustment}
            isLoading={isMutating}
            showActions={isReviewMode}
            onApprove={handleApprove}
            onReject={handleReject}
            onCancelApproved={handleCancelApproved}
          />
        ) : (
          <AttendanceAdjustmentDetailSheet
            isOpen={isDetailSheetOpen}
            onClose={() => setIsDetailSheetOpen(false)}
            adjustment={singleAdjustment}
            isLoading={isMutating}
            showActions={isReviewMode}
            onApprove={handleApprove}
            onReject={handleReject}
            onCancelApproved={handleCancelApproved}
          />
        )}

        <DirectAdjustmentDialog
          isOpen={isCreateDialogOpen}
          onClose={() => setIsCreateDialogOpen(false)}
          allowUserDateSelection
          title="Create Adjustment"
        />
      </div>
    </SidebarInset>
  );
}
